import { descontarSancion } from './marcador.js'
import type { ProblemaValidacion } from './tipos.js'

/**
 * La partida de una mesa, contada mano a mano.
 *
 * Es lo que cargan los jugadores desde el teléfono mientras juegan: quién se
 * llevó cada mano y cuántos puntos. El marcador de la partida no se guarda, se
 * suma, y esta es la única suma: la usan el API para decidir si la partida
 * terminó (y en modo automático, para cargar la mesa), el site para dibujar el
 * acumulado y el organizador para mostrar el marcador sugerido. Si cada uno
 * sumara a su manera, la mesa diría 198 en un teléfono y 200 en el salón.
 */

export type Bando = 1 | 2

export interface Mano {
  bandoGanador: Bando
  puntos: number
}

export interface ManoContada extends Mano {
  /** Desde 1, en el orden en que se jugaron. */
  numero: number
  /** Cómo quedó la partida después de esta mano. */
  bando1: number
  bando2: number
}

/**
 * Puntos que el organizador le resta a un bando en plena partida, por una
 * sanción. Solo en los torneos de Parejas con la resta en vivo encendida.
 *
 * `despuesDeMano` es cuántas manos había anotadas cuando se aplicó: la resta
 * entra en la cuenta en ese punto y no al final, porque cambia lo que le falta
 * al bando para llegar al objetivo. Cero es antes de la primera mano.
 */
export interface Resta {
  despuesDeMano: number
  bando: Bando
  puntos: number
}

/** La resta tal como llegó (con lo que traiga de más) y cómo quedó la partida después. */
export type RestaContada<R extends Resta = Resta> = R & {
  bando1: number
  bando2: number
}

export interface Partida<R extends Resta = Resta> {
  manos: ManoContada[]
  /** En el orden en que entraron en la cuenta. */
  restas: RestaContada<R>[]
  bando1: number
  bando2: number
  /** True cuando algún bando llegó al objetivo: ahí la partida terminó. */
  concluida: boolean
  /** El que llegó primero al objetivo. Nulo mientras se juega. */
  ganador: Bando | null
}

/**
 * Suma las manos en orden y dice cómo va la partida.
 *
 * Una mano la gana un solo bando, así que los dos no pueden llegar al objetivo
 * a la vez: el primero que lo alcanza gana, y con eso no hay empate posible.
 * Las manos que vinieran después del final se cuentan igual (para que la
 * pantalla muestre lo que hay), pero `validarManos` las marca como problema.
 *
 * Las restas entran después de la mano que dicen, con el mismo piso que la
 * sanción de la carga especial (`descontarSancion`): un bando no queda debajo
 * de cero. Una resta nunca hace llegar a nadie al objetivo, así que el final lo
 * siguen decidiendo las manos. Una que apunte más allá de la última mano (la
 * mano se borró después) entra al final.
 */
export function contarPartida<R extends Resta = Resta>(
  manos: readonly Mano[],
  objetivo: number,
  restas: readonly R[] = [],
): Partida<R> {
  let bando1 = 0
  let bando2 = 0
  let ganador: Bando | null = null

  const pendientes = [...restas].sort((a, b) => a.despuesDeMano - b.despuesDeMano)
  const contadas: RestaContada<R>[] = []

  /** Aplica las restas que van hasta la mano `hasta`, en el orden en que entraron. */
  function restar(hasta: number): void {
    while (pendientes.length > 0 && pendientes[0]!.despuesDeMano <= hasta) {
      const resta = pendientes.shift()!
      if (resta.bando === 1) bando1 = descontarSancion(bando1, resta.puntos)
      else bando2 = descontarSancion(bando2, resta.puntos)
      contadas.push({ ...resta, bando1, bando2 })
    }
  }

  restar(0)
  const contadasManos = manos.map((mano, i): ManoContada => {
    if (mano.bandoGanador === 1) bando1 += mano.puntos
    else bando2 += mano.puntos

    if (ganador === null) {
      if (bando1 >= objetivo) ganador = 1
      else if (bando2 >= objetivo) ganador = 2
    }
    const contada = { ...mano, numero: i + 1, bando1, bando2 }
    restar(i + 1)
    return contada
  })
  restar(Number.POSITIVE_INFINITY)

  return {
    manos: contadasManos,
    restas: contadas,
    bando1,
    bando2,
    concluida: ganador !== null,
    ganador,
  }
}

/**
 * Revisa una lista de manos antes de guardarla.
 *
 * Devuelve todos los problemas juntos, como el resto de las validaciones del
 * núcleo. La corre el API sobre la lista como quedaría DESPUÉS de agregar,
 * corregir o borrar, que es lo que permite una sola regla para las tres cosas:
 * corregir la mano 4 para que la partida termine ahí deja a la 5 y la 6 después
 * del final, y eso se rechaza igual que cargar una séptima.
 */
export function validarManos(
  manos: readonly Mano[],
  objetivo: number,
  restas: readonly Resta[] = [],
): ProblemaValidacion[] {
  const problemas: ProblemaValidacion[] = []

  const malas = manos
    .map((m, i) => ({ m, numero: i + 1 }))
    .filter(({ m }) => !Number.isInteger(m.puntos) || m.puntos < 1)
  if (malas.length > 0) {
    problemas.push({
      codigo: 'puntos_invalidos',
      mensaje: 'Cada mano suma al menos un punto, en números enteros.',
      detalle: { manos: malas.map((x) => x.numero) },
    })
  }

  const sinBando = manos
    .map((m, i) => ({ m, numero: i + 1 }))
    .filter(({ m }) => m.bandoGanador !== 1 && m.bandoGanador !== 2)
  if (sinBando.length > 0) {
    problemas.push({
      codigo: 'bando_invalido',
      mensaje: 'Cada mano la gana uno de los dos bandos.',
      detalle: { manos: sinBando.map((x) => x.numero) },
    })
  }

  if (problemas.length > 0) return problemas

  const final = contarPartida(manos, objetivo, restas).manos.findIndex(
    (m) => m.bando1 >= objetivo || m.bando2 >= objetivo,
  )
  if (final !== -1 && final < manos.length - 1) {
    problemas.push({
      codigo: 'manos_despues_del_final',
      mensaje:
        `La partida terminó en la mano ${final + 1}: ` +
        `no puede haber ${manos.length - final - 1 === 1 ? 'otra mano' : 'más manos'} después.`,
      detalle: { final: final + 1 },
    })
  }

  /* Gana el que llega primero al objetivo, y una resta puesta después de esa
     mano podría dejarlo con menos puntos que el rival: el resultado diría que
     ganó el otro. En vivo no pasa (con la partida terminada no se resta), pero
     sí al corregir una mano para que la partida termine antes de una resta. */
  const restasTarde =
    final === -1 ? [] : restas.filter((r) => r.despuesDeMano >= final + 1)
  if (restasTarde.length > 0) {
    problemas.push({
      codigo: 'resta_despues_del_final',
      mensaje:
        `Con este cambio la partida termina en la mano ${final + 1}, ` +
        `antes de ${restasTarde.length === 1 ? 'una resta que se anotó' : 'restas que se anotaron'} después. ` +
        'Avísale al organizador.',
      detalle: { final: final + 1 },
    })
  }

  return problemas
}

/**
 * Una de las cuatro sillas de la mesa, en el orden en que se juega.
 *
 * El orden es el de la ronda: la silla 1 es la de un jugador del bando 1, a su
 * derecha se sienta uno del bando 2 (silla 2), después el compañero del primero
 * (silla 3) y al final el del bando 2 que falta (silla 4). Los bandos quedan
 * alternados, que es como se sienta una mesa de dominó.
 */
export interface Silla {
  /** De 1 a 4, en el orden en que se juega. */
  silla: number
  bando: Bando
  nombre: string
}

/**
 * Sienta a las cuatro personas de la mesa en el orden en que se juega, con los
 * bandos alternados (ver `Silla`). Dentro de cada bando conservan el orden en
 * que llegan.
 *
 * Si la mesa no tiene dos personas por bando (la incompleta, el bye) devuelve
 * una lista vacía: sin cuatro sillas no hay rotación que seguir.
 */
export function sentarEnOrden(
  personas: readonly { bando: Bando; nombre: string }[],
): Silla[] {
  const uno = personas.filter((p) => p.bando === 1)
  const dos = personas.filter((p) => p.bando === 2)
  if (uno.length !== 2 || dos.length !== 2) return []
  return [uno[0]!, dos[0]!, uno[1]!, dos[1]!].map((p, i) => ({
    silla: i + 1,
    bando: p.bando,
    nombre: p.nombre,
  }))
}

/**
 * Qué silla da la salida en la mano `numero` (desde 1), sabiendo cuál la dio en
 * la primera. La salida rota hacia la derecha: cada mano sale el que se sienta
 * después del que salió en la anterior. Nulo si todavía no se anotó la salida.
 */
export function quienSale(salida: number | null, numero: number): number | null {
  if (salida === null) return null
  return ((salida - 1 + numero - 1) % 4) + 1
}

/**
 * La inversa de `quienSale`: si la silla `silla` sale en la mano `numero`, qué
 * silla dio la salida en la primera. Es lo que permite anotar la salida a mitad
 * de partida, preguntando por la mano que se va a jugar.
 */
export function salidaInicial(silla: number, numero: number): number {
  return ((((silla - 1 - (numero - 1)) % 4) + 4) % 4) + 1
}

/**
 * Los minutos que se ofrecen para el reloj de las rondas de un torneo sin
 * organizador. `null` (sin límite) va aparte: es la ausencia de reloj.
 */
export const MINUTOS_POR_RONDA = [25, 30, 35, 45] as const

/**
 * Los minutos de pausa que se ofrecen entre una ronda y la siguiente de un
 * torneo sin organizador. Cero es lo de siempre: la siguiente se arma en el
 * acto. La pausa es para ir al baño, cambiar de mesa sin correr o mirar la
 * tabla antes del sorteo.
 */
export const MINUTOS_ENTRE_RONDAS = [0, 1, 2, 3, 5, 10, 15] as const

/**
 * Si el tiempo de la ronda ya se acabó. `venceEn` es nulo en la ronda sin reloj.
 *
 * Acepta texto (lo que devuelve SQLite y lo que viaja por la red) y `Date` (lo
 * que devuelve `pg`), para que nadie tenga que convertir antes de preguntar.
 */
export function tiempoConcluido(
  venceEn: string | Date | null | undefined,
  ahora: number = Date.now(),
): boolean {
  if (venceEn === null || venceEn === undefined) return false
  return new Date(venceEn).getTime() <= ahora
}

/**
 * Quién gana la partida cuando se acaba el tiempo: el que va arriba. Nulo si
 * están empatados (cero a cero incluido), y entonces la partida sigue: la
 * próxima mano la desempata, porque una mano la gana un solo bando.
 */
export function ganadorAlTiempo(partida: Pick<Partida, 'bando1' | 'bando2'>): Bando | null {
  if (partida.bando1 === partida.bando2) return null
  return partida.bando1 > partida.bando2 ? 1 : 2
}

/**
 * Cuánto se espera la última mano después del cero. Al acabarse el tiempo la
 * mano que se está jugando se termina y se anota, y es esa la que cierra la
 * mesa; este plazo es solo para la mesa que nunca la anota (se fueron, se
 * olvidaron): pasado, el servidor la cierra con el marcador que lleva.
 */
export const ESPERA_ULTIMA_MANO_MS = 10 * 60_000

/**
 * Si ya pasó el plazo de la última mano (ver `ESPERA_ULTIMA_MANO_MS`). Nunca
 * antes de `tiempoConcluido`.
 */
export function esperaUltimaManoConcluida(
  venceEn: string | Date | null | undefined,
  ahora: number = Date.now(),
): boolean {
  return tiempoConcluido(venceEn, ahora - ESPERA_ULTIMA_MANO_MS)
}
